import React, { useState } from 'react';
import { Mail, Github, ChevronRight } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { sendEmailDirect } from '@/lib/emailjs';

interface ContactSectionProps {
  className?: string;
}

interface FormData {
  name: string;
  email: string;
  company: string;
  subject: string;
  message: string;
}

interface FormErrors {
  name?: string;
  email?: string;
  subject?: string;
  message?: string;
}

const ContactSection: React.FC<ContactSectionProps> = ({ className }) => {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    company: '',
    subject: '',
    message: '',
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<
    'idle' | 'success' | 'error'
  >('idle');

  const contactMethods = [
    {
      type: 'Email',
      value: 'jermarino16@gmail.com',
      href: 'mailto:jermarino16@gmail.com',
      icon: Mail,
      description: 'Best for detailed discussions and project inquiries',
      available: '24/7',
    },
    {
      type: 'GitHub',
      value: 'github.com/jermarino16',
      href: 'https://github.com/jermarino16',
      icon: Github,
      description: 'Code repositories and technical collaboration',
      available: 'Open Source',
    },
  ];

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.subject.trim()) {
      newErrors.subject = 'Subject is required';
    }

    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    } else if (formData.message.trim().length < 10) {
      newErrors.message = 'Message must be at least 10 characters long';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // Clear error when user starts typing
    if (errors[name as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      // Send email using EmailJS
      await sendEmailDirect(formData);

      setSubmitStatus('success');
      setFormData({
        name: '',
        email: '',
        company: '',
        subject: '',
        message: '',
      });
    } catch (error) {
      console.error('Failed to send email:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id='contact' className={cn('py-20 bg-muted/30', className)}>
      <div className='container mx-auto px-4'>
        {/* Section Header */}
        <div className='text-center mb-16'>
          <h2 className='text-3xl sm:text-4xl font-bold text-foreground mb-4'>
            Let's Work Together
          </h2>
          <p className='text-lg text-muted-foreground max-w-3xl mx-auto mb-8'>
            Ready to bring your next project to life? I'm always interested in
            discussing new opportunities, innovative projects, and ways to
            create exceptional software solutions. Let's connect and explore how
            we can collaborate.
          </p>

          {/* Availability Status */}
          <div className='flex justify-center mb-8'>
            <Badge variant='outline' className='px-4 py-2 text-sm'>
              <span className='w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse'></span>
              Available for New Projects
            </Badge>
          </div>
        </div>

        <div className='grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-6xl mx-auto'>
          {/* Contact Form */}
          <Card className='h-fit'>
            <CardHeader>
              <CardTitle className='text-2xl text-primary'>
                Send a Message
              </CardTitle>
              <p className='text-muted-foreground'>
                Fill out the form below and I'll get back to you within 24
                hours.
              </p>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className='space-y-6'>
                <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
                  <div>
                    <label
                      htmlFor='name'
                      className='block text-sm font-medium text-foreground mb-2'
                    >
                      Full Name *
                    </label>
                    <input
                      type='text'
                      id='name'
                      name='name'
                      value={formData.name}
                      onChange={handleInputChange}
                      className={cn(
                        'w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent',
                        errors.name ? 'border-red-500' : 'border-input'
                      )}
                      placeholder='Your full name'
                    />
                    {errors.name && (
                      <p className='text-red-500 text-xs mt-1'>{errors.name}</p>
                    )}
                  </div>

                  <div>
                    <label
                      htmlFor='email'
                      className='block text-sm font-medium text-foreground mb-2'
                    >
                      Email Address *
                    </label>
                    <input
                      type='email'
                      id='email'
                      name='email'
                      value={formData.email}
                      onChange={handleInputChange}
                      className={cn(
                        'w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent',
                        errors.email ? 'border-red-500' : 'border-input'
                      )}
                      placeholder='your.email@example.com'
                    />
                    {errors.email && (
                      <p className='text-red-500 text-xs mt-1'>
                        {errors.email}
                      </p>
                    )}
                  </div>
                </div>

                <div>
                  <label
                    htmlFor='company'
                    className='block text-sm font-medium text-foreground mb-2'
                  >
                    Company / Organization
                  </label>
                  <input
                    type='text'
                    id='company'
                    name='company'
                    value={formData.company}
                    onChange={handleInputChange}
                    className='w-full px-3 py-2 border border-input rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent'
                    placeholder='Your company or organization'
                  />
                </div>

                <div>
                  <label
                    htmlFor='subject'
                    className='block text-sm font-medium text-foreground mb-2'
                  >
                    Subject *
                  </label>
                  <input
                    type='text'
                    id='subject'
                    name='subject'
                    value={formData.subject}
                    onChange={handleInputChange}
                    className={cn(
                      'w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent',
                      errors.subject ? 'border-red-500' : 'border-input'
                    )}
                    placeholder='What would you like to discuss?'
                  />
                  {errors.subject && (
                    <p className='text-red-500 text-xs mt-1'>
                      {errors.subject}
                    </p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor='message'
                    className='block text-sm font-medium text-foreground mb-2'
                  >
                    Message *
                  </label>
                  <textarea
                    id='message'
                    name='message'
                    value={formData.message}
                    onChange={handleInputChange}
                    rows={5}
                    className={cn(
                      'w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-vertical',
                      errors.message ? 'border-red-500' : 'border-input'
                    )}
                    placeholder='Tell me about your project, goals, or how I can help...'
                  />
                  {errors.message && (
                    <p className='text-red-500 text-xs mt-1'>
                      {errors.message}
                    </p>
                  )}
                </div>

                <button
                  type='submit'
                  disabled={isSubmitting}
                  className={cn(
                    'w-full bg-primary text-primary-foreground hover:bg-primary/90 px-6 py-3 rounded-md font-medium transition-colors',
                    isSubmitting && 'opacity-50 cursor-not-allowed'
                  )}
                >
                  {isSubmitting ? 'Sending Message...' : 'Send Message'}
                </button>

                {submitStatus === 'success' && (
                  <div className='p-4 bg-green-50 border border-green-200 rounded-md'>
                    <p className='text-green-800 text-sm'>
                      Message sent successfully! I'll get back to you within 24
                      hours.
                    </p>
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className='p-4 bg-red-50 border border-red-200 rounded-md'>
                    <p className='text-red-800 text-sm'>
                      Failed to send message. Please try again or contact me
                      directly via email.
                    </p>
                  </div>
                )}
              </form>
            </CardContent>
          </Card>

          {/* Contact Information */}
          <div className='space-y-8'>
            <Card>
              <CardHeader>
                <CardTitle className='text-2xl text-primary'>
                  Get In Touch
                </CardTitle>
                <p className='text-muted-foreground'>
                  Choose your preferred method of communication. I'm responsive
                  across all channels.
                </p>
              </CardHeader>
              <CardContent className='space-y-6'>
                {contactMethods.map((method, index) => {
                  const IconComponent = method.icon;
                  return (
                    <div
                      key={index}
                      className='flex items-start gap-4 p-4 rounded-lg border hover:bg-muted/50 transition-colors'
                    >
                      <div className='text-primary'>
                        <IconComponent className='w-6 h-6' />
                      </div>
                      <div className='flex-1'>
                        <div className='flex items-center gap-2 mb-1'>
                          <h3 className='font-semibold text-foreground'>
                            {method.type}
                          </h3>
                          <Badge variant='secondary' className='text-xs'>
                            {method.available}
                          </Badge>
                        </div>
                        <a
                          href={method.href}
                          target={
                            method.type !== 'Email' && method.type !== 'Phone'
                              ? '_blank'
                              : undefined
                          }
                          rel={
                            method.type !== 'Email' && method.type !== 'Phone'
                              ? 'noopener noreferrer'
                              : undefined
                          }
                          className='text-primary hover:underline font-medium'
                        >
                          {method.value}
                        </a>
                        <p className='text-sm text-muted-foreground mt-1'>
                          {method.description}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            </Card>

            {/* Response Time & Preferences */}
            <Card>
              <CardHeader>
                <CardTitle className='text-xl text-primary'>
                  Response Time & Preferences
                </CardTitle>
              </CardHeader>
              <CardContent className='space-y-4'>
                <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
                  <div className='text-center p-4 bg-muted/30 rounded-lg'>
                    <div className='text-2xl font-bold text-primary mb-1'>
                      &lt; 24hrs
                    </div>
                    <div className='text-sm text-muted-foreground'>
                      Email Response
                    </div>
                  </div>
                  <div className='text-center p-4 bg-muted/30 rounded-lg'>
                    <div className='text-2xl font-bold text-primary mb-1'>
                      &lt; 2hrs
                    </div>
                    <div className='text-sm text-muted-foreground'>
                      Urgent Matters
                    </div>
                  </div>
                </div>

                <div className='space-y-3'>
                  <h4 className='font-semibold text-foreground'>
                    Best Times to Reach Me:
                  </h4>
                  <ul className='space-y-2 text-sm text-muted-foreground'>
                    <li className='flex items-center gap-2'>
                      <ChevronRight className='w-4 h-4 text-primary' />
                      <span>Monday - Friday: 9:00 AM - 6:00 PM PST</span>
                    </li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Call to Action */}
        <div className='text-center mt-16'>
          <h3 className='text-2xl font-semibold text-foreground mb-4'>
            Ready to Start Your Next Project?
          </h3>
          <p className='text-muted-foreground max-w-2xl mx-auto mb-8'>
            Whether you're looking to build a new application, optimize existing
            systems, or need technical leadership for your team, I'm here to
            help turn your vision into reality.
          </p>
          <div className='flex flex-col sm:flex-row gap-4 justify-center'>
            <a
              href='mailto:jermarino16@gmail.com'
              className='bg-primary text-primary-foreground hover:bg-primary/90 px-8 py-3 rounded-md font-medium transition-colors'
            >
              Start a Conversation
            </a>
            <a
              href='/resume.pdf'
              target='_blank'
              rel='noopener noreferrer'
              className='bg-muted text-muted-foreground hover:bg-muted/80 px-8 py-3 rounded-md font-medium transition-colors'
            >
              View Resume
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
